import { ItemLayout } from '@/shared/components/ItemLayout/ItemLayout'
import { ColumnDef } from '@tanstack/react-table'
import { PayoutIcon } from '@/shared/components'
import { PaidWithdrawModel, WithdrawStatusIcons, WithdrawStatusTitles } from '../model'
import { formatDate } from '@/shared/lib/date'
import { formatRequisite } from '@/shared/lib/requisite/formatRequisite'
import { getBankMeta } from '@/shared/utils/getBankMeta'
import { formatCurrency } from '@/shared/lib/currency'
import { Currencies } from '@/entities/curency'
import { SortDirection, SortIcon } from '@/entities/sort'
import { BaseButton } from '@/shared/components'
import { FileText } from 'lucide-react'
import { getShortenId } from '@/shared/utils/getShortenId'
import { Link } from 'react-router-dom'
import { CopyBlock } from '@/shared/components/CopyBlock/CopyBlock'

interface ColumnsCompletedProps {
  onManageReceipts?: (withdrawId: string) => void
  includeReceipts?: boolean
  sort?: {
    sortCreatedAt?: SortDirection
    sortAmountTo?: SortDirection
  }
  toggleSort?: (key: keyof NonNullable<ColumnsCompletedProps['sort']>) => void
  showRate: boolean
}

export const getColumnsCompleted = (
  props?: ColumnsCompletedProps
): ColumnDef<PaidWithdrawModel>[] => {
  const { onManageReceipts, includeReceipts = true, sort, toggleSort, showRate } = props || {}

  let columns: ColumnDef<PaidWithdrawModel>[] = [
    {
      accessorKey: 'id',
      header: 'ID заявки',
      cell: ({ row }) => {
        const id = row.original.id
        return (
          <CopyBlock text={id} title={id}>
            <Link to={id} className="text-primary">
              {getShortenId(id)}
            </Link>
          </CopyBlock>
        )
      },
    },
    {
      accessorKey: 'payout',
      header: () => {
        return (
          <div className="flex items-center gap-1" onClick={() => toggleSort?.('sortCreatedAt')}>
            <span>Заявка</span>
            {sort && <SortIcon direction={sort.sortCreatedAt} />}
          </div>
        )
      },
      cell: ({ row }) => {
        const createdAt = row.original.createdAt || ''
        return (
          <Link to={row.original.id}>
            <ItemLayout
              icon={<PayoutIcon status={WithdrawStatusIcons[row.original.withdrawStatus]} />}
              title={
                <span className="mr-[10px]">
                  {WithdrawStatusTitles[row.original.withdrawStatus]}
                </span>
              }
              subtitle={new Date(createdAt).toLocaleString('ru-RU')}
            />
          </Link>
        )
      },
    },
    {
      accessorKey: 'requisite',
      header: 'Реквизит',
      cell: ({ row }) => {
        const { title } = getBankMeta(row.original.bankToName)
        return (
          <div className="flex flex-col">
            <CopyBlock text={row.original.paymentRequisitValue}>
              {formatRequisite(row.original.paymentRequisitValue)}
            </CopyBlock>
            <span className="text-text-secondary">{title}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'amount',
      header: () => {
        return (
          <div className="flex items-center gap-1" onClick={() => toggleSort?.('sortAmountTo')}>
            <span>Сумма</span>
            {sort && <SortIcon direction={sort.sortAmountTo} />}
          </div>
        )
      },
      cell: ({ row }) => {
        return (
          <div className="flex flex-col">
            {(row.original.factAmountTo && row.original.amountTo.toFixed(2) === row.original.factAmountTo.toFixed(2))?<CopyBlock text={row.original.amountTo?.toFixed(2)}>
              {formatCurrency(row.original.amountTo ?? 0, row.original.currencyTo)}
            </CopyBlock>
            : (
              <CopyBlock text={row.original.factAmountTo?.toFixed(2)}>
                {formatCurrency(row.original.factAmountTo ?? 0, row.original.currencyTo)}
              </CopyBlock>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'rate',
      header: 'Курс',
      cell: ({ row }) => {
        const rate = formatCurrency(
          row.original.traderExchangeRateFromTo ?? row.original.exchangeRateFromTo
        )
        const time = formatDate(row.original.confirmedByTraderAt, 'timeOnly')
        return (
          <div className="flex flex-col">
            {rate && time && (
              <span>
                {rate} на {time}
              </span>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'profit',
      header: 'Вознаграждение',
      cell: ({ row }) => {
        return (
          <div className="flex flex-col">
            <span className="font-medium">
              {formatCurrency(row.original.factTraderCommissionTo, Currencies.USDT)}
            </span>
          </div>
        )
      },
    },
    {
      accessorKey: 'amountTo',
      header: 'Начислено',
      cell: ({ row }) => {
        return (
          <div className="flex flex-col">
            <span className="font-medium">
              {formatCurrency(
                row.original.factTraderCommissionTo + row.original.traderAmountFrom,
                Currencies.USDT
              )}
            </span>
          </div>
        )
      },
    },
  ]

  if (!showRate) {
    columns = columns.filter((c) => 'accessorKey' in c && c.accessorKey !== 'rate')
  }

  if (includeReceipts) {
    columns.push({
      accessorKey: 'receipts',
      header: '',
      cell: ({ row }) => {
        return (
          <BaseButton
            variant="ghost"
            padding="xs"
            onClick={() => onManageReceipts?.(row.original.id)}
            icon={<FileText className="w-4 h-4" />}
            iconPosition="left"
          >
            Управление чеками
          </BaseButton>
        )
      },
    })
  }

  return columns
}
